import { socketService } from './SocketService';
import type { VoiceSignalData } from '@shared/types/events.types';

export class VoiceChat {
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private peerConnection: RTCPeerConnection | null = null;
  // private isInitiator: boolean = false; // Not used yet
  private pushToTalkKey: string = 'Space';
  private isTalking: boolean = false;
  private remotePlayerId: string | null = null;
  private remoteAudioElement: HTMLAudioElement | null = null;
  
  constructor() {
    this.setupSocketHandlers();
    this.setupPushToTalk();
  }
  
  private setupSocketHandlers(): void {
    socketService.on('voiceSignal', async (data: VoiceSignalData) => {
      console.log('[VoiceChat] Received voice signal:', data.type);
      
      if (data.type === 'offer') {
        await this.handleOffer(data.data as RTCSessionDescriptionInit, data.from!);
      } else if (data.type === 'answer') {
        await this.handleAnswer(data.data as RTCSessionDescriptionInit);
      } else if (data.type === 'ice-candidate') {
        await this.handleIceCandidate(data.data as RTCIceCandidateInit);
      }
    });
    
    socketService.on('voiceChatStatus', (data: any) => {
      console.log('[VoiceChat] Voice chat status:', data);
      // Update UI to show peer's voice status
      this.updatePeerVoiceStatus(data.playerId, data.enabled);
    });
    
    socketService.on('voiceError', (data: any) => {
      console.error('[VoiceChat] Voice error:', data.error);
      // Show error to user
      this.showError(data.error);
    });
  }
  
  async initializeVoiceChat(remotePlayerId: string): Promise<void> {
    try {
      console.log('[VoiceChat] Initializing voice chat with:', remotePlayerId);
      this.remotePlayerId = remotePlayerId;
      
      // Get user media
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      });
      
      console.log('[VoiceChat] Got local stream');
      
      // Mute by default (push-to-talk)
      this.muteLocalAudio(true);
      
      // Create peer connection
      this.peerConnection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      });
      
      // Add local stream
      this.localStream.getTracks().forEach(track => {
        this.peerConnection!.addTrack(track, this.localStream!);
      });
      
      // Handle remote stream
      this.peerConnection.ontrack = (event) => {
        console.log('[VoiceChat] Received remote track');
        this.remoteStream = event.streams[0];
        this.playRemoteAudio();
      };
      
      // Handle ICE candidates
      this.peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('[VoiceChat] Sending ICE candidate');
          socketService.sendVoiceSignal({
            type: 'ice-candidate',
            data: event.candidate,
            to: remotePlayerId
          });
        }
      };
      
      // Connection state monitoring
      this.peerConnection.onconnectionstatechange = () => {
        console.log('[VoiceChat] Connection state:', this.peerConnection?.connectionState);
        this.updateConnectionStatus(this.peerConnection?.connectionState || 'disconnected');
      };
      
      // Create offer
      // this.isInitiator = true;
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      
      console.log('[VoiceChat] Sending offer');
      socketService.sendVoiceSignal({
        type: 'offer',
        data: offer,
        to: remotePlayerId
      });
      
      // Notify server that voice chat is enabled
      socketService.toggleVoiceChat(true);
      
    } catch (error) {
      console.error('[VoiceChat] Failed to initialize voice chat:', error);
      this.showError('Failed to initialize voice chat. Please check your microphone permissions.');
      throw error;
    }
  }
  
  private async handleOffer(offer: RTCSessionDescriptionInit, fromPlayerId: string): Promise<void> {
    console.log('[VoiceChat] Handling offer from:', fromPlayerId);
    this.remotePlayerId = fromPlayerId;
    
    if (!this.peerConnection) {
      // Initialize peer connection if not initiator
      await this.setupPeerConnection();
    }
    
    await this.peerConnection!.setRemoteDescription(offer);
    const answer = await this.peerConnection!.createAnswer();
    await this.peerConnection!.setLocalDescription(answer);
    
    console.log('[VoiceChat] Sending answer');
    socketService.sendVoiceSignal({
      type: 'answer',
      data: answer,
      to: fromPlayerId
    });
  }
  
  private async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    console.log('[VoiceChat] Handling answer');
    if (this.peerConnection) {
      await this.peerConnection.setRemoteDescription(answer);
    }
  }
  
  private async handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    console.log('[VoiceChat] Handling ICE candidate');
    if (this.peerConnection) {
      await this.peerConnection.addIceCandidate(candidate);
    }
  }
  
  private async setupPeerConnection(): Promise<void> {
    console.log('[VoiceChat] Setting up peer connection');
    
    // Get user media
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      },
      video: false
    });
    
    // Mute by default (push-to-talk)
    this.muteLocalAudio(true);
    
    // Create peer connection
    this.peerConnection = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });
    
    // Add local stream
    this.localStream.getTracks().forEach(track => {
      this.peerConnection!.addTrack(track, this.localStream!);
    });
    
    // Handle remote stream
    this.peerConnection.ontrack = (event) => {
      console.log('[VoiceChat] Received remote track');
      this.remoteStream = event.streams[0];
      this.playRemoteAudio();
    };
    
    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.remotePlayerId) {
        console.log('[VoiceChat] Sending ICE candidate');
        socketService.sendVoiceSignal({
          type: 'ice-candidate',
          data: event.candidate,
          to: this.remotePlayerId
        });
      }
    };
    
    // Connection state monitoring
    this.peerConnection.onconnectionstatechange = () => {
      console.log('[VoiceChat] Connection state:', this.peerConnection?.connectionState);
      this.updateConnectionStatus(this.peerConnection?.connectionState || 'disconnected');
    };
    
    // Notify server that voice chat is enabled
    socketService.toggleVoiceChat(true);
  }
  
  private setupPushToTalk(): void {
    // Keyboard events for push-to-talk
    document.addEventListener('keydown', (event) => {
      // Don't trigger push-to-talk if user is typing in an input field
      const target = event.target as HTMLElement;
      const isInputField = target && (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true'
      );
      
      if (event.code === this.pushToTalkKey && !this.isTalking && !event.repeat && !isInputField) {
        event.preventDefault();
        this.isTalking = true;
        this.muteLocalAudio(false);
        this.updateUITalkingStatus(true);
      }
    });
    
    document.addEventListener('keyup', (event) => {
      // Don't trigger push-to-talk if user is typing in an input field
      const target = event.target as HTMLElement;
      const isInputField = target && (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true'
      );
      
      if (event.code === this.pushToTalkKey && this.isTalking && !isInputField) {
        event.preventDefault();
        this.isTalking = false;
        this.muteLocalAudio(true);
        this.updateUITalkingStatus(false);
      }
    });
    
    // Handle window blur (release key when alt-tabbing)
    window.addEventListener('blur', () => {
      if (this.isTalking) {
        this.isTalking = false;
        this.muteLocalAudio(true);
        this.updateUITalkingStatus(false);
      }
    });
  }
  
  private muteLocalAudio(mute: boolean): void {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !mute;
      });
    }
  }
  
  private playRemoteAudio(): void {
    if (!this.remoteStream) return;
    
    // Create or update audio element
    if (!this.remoteAudioElement) {
      this.remoteAudioElement = document.createElement('audio');
      this.remoteAudioElement.autoplay = true;
      this.remoteAudioElement.style.display = 'none';
      document.body.appendChild(this.remoteAudioElement);
    }
    
    this.remoteAudioElement.srcObject = this.remoteStream;
  }
  
  private updateUITalkingStatus(talking: boolean): void {
    // Dispatch custom event for UI components to listen to
    window.dispatchEvent(new CustomEvent('voiceChatTalking', { 
      detail: { talking } 
    }));
  }
  
  private updateConnectionStatus(status: string): void {
    window.dispatchEvent(new CustomEvent('voiceChatConnectionStatus', { 
      detail: { status } 
    }));
  }
  
  private updatePeerVoiceStatus(playerId: string, enabled: boolean): void {
    window.dispatchEvent(new CustomEvent('peerVoiceStatus', { 
      detail: { playerId, enabled } 
    }));
  }
  
  private showError(error: string): void {
    window.dispatchEvent(new CustomEvent('voiceChatError', { 
      detail: { error } 
    }));
  }
  
  // Public methods
  disconnect(): void {
    console.log('[VoiceChat] Disconnecting voice chat');
    
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
    }
    if (this.peerConnection) {
      this.peerConnection.close();
    }
    if (this.remoteAudioElement) {
      this.remoteAudioElement.remove();
    }
    
    this.localStream = null;
    this.remoteStream = null;
    this.peerConnection = null;
    this.remoteAudioElement = null;
    this.remotePlayerId = null;
    
    // Notify server that voice chat is disabled
    socketService.toggleVoiceChat(false);
  }
  
  isConnected(): boolean {
    return this.peerConnection?.connectionState === 'connected';
  }
  
  getRemotePlayerId(): string | null {
    return this.remotePlayerId;
  }
}

// Singleton instance
export const voiceChat = new VoiceChat();